const SCALAPAY_LOADER = 'https://cdn.scalapay.com/widget/scalapay-widget-loader.js?version=V5';
let scalapayLoader;
let scalapayRetry = 0;

// Shared across variant replacements and popup reopens. No third-party request before a click.
function loadScalapay() {
  if (customElements.get('scalapay-widget')) return Promise.resolve();
  if (scalapayLoader) return scalapayLoader;

  scalapayLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => fail(), 15000);
    const fail = () => {
      window.clearTimeout(timeout);
      script.remove();
      scalapayLoader = undefined;
      scalapayRetry += 1;
      reject(new Error('Scalapay could not be loaded'));
    };

    script.type = 'module';
    // Browsers cache failed module fetches by URL; a retry needs a fresh URL.
    script.src = scalapayRetry ? `${SCALAPAY_LOADER}&retry=${scalapayRetry}` : SCALAPAY_LOADER;
    script.addEventListener('error', fail, { once: true });
    customElements.whenDefined('scalapay-widget').then(() => {
      window.clearTimeout(timeout);
      script.removeEventListener('error', fail);
      resolve();
    });
    document.head.append(script);
  });

  return scalapayLoader;
}

// Include the widget's shadow DOM so keyboard users can reach all payment options.
function focusableElements(root) {
  return Array.from(root.children).flatMap((element) => {
    if (element.hidden || window.getComputedStyle(element).display === 'none') return [];
    const children = focusableElements(element.shadowRoot || element);
    return element.matches('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]')
      ? [element, ...children]
      : children;
  });
}

class ExaProductInstallments extends HTMLElement {
  connectedCallback() {
    this.popup = this.querySelector('[data-scalapay-popup]');
    this.trigger = this.querySelector('[data-scalapay-open]');
    this.panel = this.popup.querySelector('[role="dialog"]');
    this.status = this.popup.querySelector('[data-scalapay-status]');
    this.widgetContainer = this.popup.querySelector('[data-scalapay-widget]');
    this.retry = this.popup.querySelector('[data-scalapay-retry]');

    this.trigger.addEventListener('click', this.open);
    this.popup.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.close(false);
    this.trigger?.removeEventListener('click', this.open);
    this.popup?.removeEventListener('click', this.handleClick);
    this.popup?.remove();
  }

  open = () => {
    if (!this.popup.hidden) return;

    // Keep the overlay outside the PDP stacking context. Scalapay's own detail
    // modal is mounted on document.body, so a native top-layer dialog would hide it.
    document.body.append(this.popup);
    this.popup.hidden = false;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('focusin', this.handleFocus);
    this.popup.querySelector('[data-scalapay-close]').focus();
    this.loadWidget();
  };

  close(restoreFocus = true) {
    if (!this.popup || this.popup.hidden) return;

    this.popup.hidden = true;
    // Invalidate an in-flight load and dispose any vendor modal on variant replacement.
    this.loadAttempt = undefined;
    this.widgetContainer.replaceChildren();
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('focusin', this.handleFocus);
    document.documentElement.style.overflow = this.previousOverflow;
    if (restoreFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === this.popup || event.target.closest('[data-scalapay-close]')) this.close();
    if (event.target.closest('[data-scalapay-retry]')) this.loadWidget();
  };

  handleFocus = (event) => {
    if (document.querySelector('scalapay-modal')) return;
    if (!this.popup.contains(event.target)) this.panel.focus();
  };

  handleKeydown = (event) => {
    // The provider manages focus and Escape while its payment detail modal is open.
    if (document.querySelector('scalapay-modal')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const elements = focusableElements(this.panel);
    const first = elements[0];
    const last = elements.at(-1);
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || active === this.panel)) {
      event.preventDefault();
      first?.focus();
    }
  };

  async loadWidget() {
    const attempt = {};
    this.loadAttempt = attempt;
    this.retry.hidden = true;
    this.status.hidden = false;
    this.status.textContent = 'Caricamento delle opzioni Scalapay…';

    if (!this.dataset.merchantToken) {
      this.status.textContent = 'Le informazioni Scalapay non sono al momento disponibili. Puoi verificare le opzioni di pagamento al checkout.';
      return;
    }

    try {
      await loadScalapay();
      if (!this.isConnected || this.popup.hidden || this.loadAttempt !== attempt) return;

      const widget = document.createElement('scalapay-widget');
      widget.setAttribute('merchant-token', this.dataset.merchantToken);
      widget.setAttribute('amount', this.dataset.amount);
      widget.setAttribute('amount-separator', '.');
      widget.setAttribute('locale', 'it');
      widget.setAttribute('environment', 'production');
      widget.setAttribute('type', 'product');
      this.widgetContainer.replaceChildren(widget);
      this.status.hidden = true;
    } catch {
      if (!this.isConnected || this.popup.hidden || this.loadAttempt !== attempt) return;
      this.status.textContent = 'Non è stato possibile caricare Scalapay. Riprova oppure verifica le opzioni di pagamento al checkout.';
      this.retry.hidden = false;
    }
  }
}

if (!customElements.get('exa-product-installments')) {
  customElements.define('exa-product-installments', ExaProductInstallments);
}
